// The theme token map (#399).
//
// Every value here is a `var(--c-…)` reference; the COLOURS themselves live in
// src/global.css, which declares the light palette on `:root` and the dark
// palette twice — once behind `prefers-color-scheme` (that is what makes theme
// "system" work with zero JavaScript and therefore with no flash of the wrong
// theme) and once behind `:root[data-theme="dark"]` so an explicit choice wins
// in both directions. src/lib/theme.contrast.test.ts parses that file, asserts
// the two dark blocks agree, and checks every text/background pair in BOTH
// palettes against WCAG AA 4.5:1.
//
// Why indirection rather than two palette objects: the app is styled with
// inline style objects (src/lib/styles.ts), which no stylesheet can override.
// A second object would mean re-rendering the tree on every theme change and
// threading a palette through every component. A custom property is repainted
// by the browser when the attribute on <html> changes — nothing re-renders.
// The CSP (`style-src 'self'`) is untouched by this: React writes styles
// through the CSSOM, which CSP does not police, and no <style> tag is injected.
//
// H-5, unchanged and now true of both palettes: `gold` is a background/border
// colour only (1.67:1 on white); text on a light surface uses `goldText`.
// Button LABEL colours could not stay constant across themes — white reads on
// the light theme's dark red and vanishes on the dark theme's light red — so
// they are their own tokens, `onDanger` / `onSuccess`.
export const C = {
  bg: 'var(--c-bg)',
  bgCard: 'var(--c-bg-card)',
  bgCardHover: 'var(--c-bg-card-hover)',
  bgInput: 'var(--c-bg-input)',
  bgSidebar: 'var(--c-bg-sidebar)',
  bgSidebarHover: 'var(--c-bg-sidebar-hover)',

  border: 'var(--c-border)',
  borderFocus: 'var(--c-border-focus)',

  gold: 'var(--c-gold)',
  goldText: 'var(--c-gold-text)',
  goldLight: 'var(--c-gold-light)',
  goldDim: 'var(--c-gold-dim)',
  goldBg: 'var(--c-gold-bg)',

  text: 'var(--c-text)',
  textSecondary: 'var(--c-text-secondary)',
  textMuted: 'var(--c-text-muted)',
  textDim: 'var(--c-text-dim)',

  sideText: 'var(--c-side-text)',
  sideTextDim: 'var(--c-side-text-dim)',
  sideTextActive: 'var(--c-side-text-active)',

  success: 'var(--c-success)',
  successBg: 'var(--c-success-bg)',
  successLight: 'var(--c-success-light)',
  danger: 'var(--c-danger)',
  dangerBg: 'var(--c-danger-bg)',
  dangerLight: 'var(--c-danger-light)',
  warn: 'var(--c-warn)',
  warnBg: 'var(--c-warn-bg)',
  warnLight: 'var(--c-warn-light)',
  info: 'var(--c-info)',
  infoBg: 'var(--c-info-bg)',
  infoLight: 'var(--c-info-light)',

  // StatusBadge's two non-status badge families, which used to be literals in
  // the component: FINAL_DETERMINATION (special) and CLOSED / EXPIRED /
  // UNCHANGED (neutral). A literal has no dark counterpart.
  special: 'var(--c-special)',
  specialBg: 'var(--c-special-bg)',
  neutral: 'var(--c-neutral)',
  neutralBg: 'var(--c-neutral-bg)',

  onDanger: 'var(--c-on-danger)',
  onSuccess: 'var(--c-on-success)',

  white: 'var(--c-white)',
  black: 'var(--c-black)',
} as const;

export type ThemeToken = keyof typeof C;

export const THEME_MODES = ['light', 'dark', 'system'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const DENSITIES = ['comfortable', 'compact'] as const;
export type Density = (typeof DENSITIES)[number];

export function isThemeMode(v: unknown): v is ThemeMode {
  return typeof v === 'string' && (THEME_MODES as readonly string[]).includes(v);
}

export function isDensity(v: unknown): v is Density {
  return typeof v === 'string' && (DENSITIES as readonly string[]).includes(v);
}

/**
 * Stamp the chosen theme on <html>. "system" REMOVES the attribute rather than
 * resolving the media query in JS: global.css's `prefers-color-scheme` block
 * already carries that case, so leaving it to CSS is both less code and live —
 * a user who flips their OS theme with the tab open re-paints immediately,
 * without us subscribing to a MediaQueryList.
 */
export function applyTheme(mode: ThemeMode, root: HTMLElement = document.documentElement): void {
  if (mode === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', mode);
}

/**
 * Stamp table density on <html>. Comfortable is the default and reproduces the
 * pre-#399 cell padding exactly, so it clears the attribute the same way.
 */
export function applyDensity(density: Density, root: HTMLElement = document.documentElement): void {
  if (density === 'comfortable') root.removeAttribute('data-density');
  else root.setAttribute('data-density', density);
}
